import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase-config";
import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy, // leave this to eventually order by screen name
} from "firebase/firestore";
import { Avatar } from "./Avatar";

import "../styles/Chat.scss";

export const Users4 = ({ refreshChatList, setIsOpenSearchUser, openChatById, setIsInUsers }) => {
  const [users, setUsers] = useState([]);
  const usersRef = collection(db, "users");
  const [avatars, setAvatars] = useState({});
  const [inputFocus, setInputFocus] = useState(false);
  const [newSearch, setNewSearch] = useState("");
  // Which user row is currently being opened (find/create + navigate), for a
  // per-row "Opening…" state and to guard against double-taps.
  const [openingId, setOpeningId] = useState(null);

  useEffect(() => {
    const queryMessages = query(
      usersRef /*,
      where("room", "==", room),
      orderBy("createdAt")*/
    );
    const unsuscribe = onSnapshot(queryMessages, (snapshot) => {
      let users = [];
      let avatars = [];
      snapshot.forEach((doc) => {
        avatars.push(doc.id);
        users.push({ ...doc.data(), id: doc.id });
      });
      
      // Use Promise.all to properly wait for all async operations
      const avatarPromises = avatars.map(async (uid) => {
        try {
          const docRef = doc(db, "users", uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            return {
              uid,
              data: {
                displayName: docSnap.data().displayName,
                photoURL: docSnap.data().photoURL,
                email: docSnap.data().email,
                lastOnline: docSnap.data().lastOnline,
              }
            };
          } else {
            // Return null for non-existent documents
            return { uid, data: null };
          }
        } catch (error) {
          console.error(`Error fetching avatar for user ${uid}:`, error);
          return { uid, data: null };
        }
      });
      
      Promise.all(avatarPromises)
        .then(function (avatarResults) {
          // Convert array of results back to object format
          const avatarObj = {};
          avatarResults.forEach(result => {
            if (result.data) {
              avatarObj[result.uid] = result.data;
            }
          });
          setAvatars(avatarObj);
          
          // Sort users by online status first, then by lastOnline
          const sortedUsers = users.sort((a, b) => {
            const aLastOnline = avatarObj[a.id]?.lastOnline;
            const bLastOnline = avatarObj[b.id]?.lastOnline;
            
            // Check if users are online (within last 5 minutes)
            const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
            const aIsOnline = aLastOnline && aLastOnline.seconds && (aLastOnline.seconds * 1000) > fiveMinutesAgo;
            const bIsOnline = bLastOnline && bLastOnline.seconds && (bLastOnline.seconds * 1000) > fiveMinutesAgo;
            
            // Both online - sort by most recent activity
            if (aIsOnline && bIsOnline) {
              return bLastOnline.seconds - aLastOnline.seconds;
            }
            // Only a is online - a comes first
            if (aIsOnline && !bIsOnline) {
              return -1;
            }
            // Only b is online - b comes first
            if (!aIsOnline && bIsOnline) {
              return 1;
            }
            
            // Neither is online - sort by lastOnline timestamp
            // Both have lastOnline - sort by most recent first
            if (aLastOnline && bLastOnline) {
              return bLastOnline.seconds - aLastOnline.seconds;
            }
            // Only a has lastOnline - a comes first
            if (aLastOnline && !bLastOnline) {
              return -1;
            }
            // Only b has lastOnline - b comes first
            if (!aLastOnline && bLastOnline) {
              return 1;
            }
            // Neither has lastOnline - sort alphabetically by displayName
            const aName = (a.displayName || a.email || '').toLowerCase();
            const bName = (b.displayName || b.email || '').toLowerCase();
            return aName.localeCompare(bName);
          });
          
          setUsers(sortedUsers);
        })
        .catch(error => {
          console.error("Error loading avatars:", error);
        });
      // .then(function () {
      //   // return new Promise((resolve, reject) => {
      //     //                 item.photoURL === "https://thisisobah.com/img/obah-logo.png"
      //   setTimeout(() => {
      //     console.log("users", users);
      //     // resolve(users)
      //     users.forEach(async (item) => {
      //       if (
      //         item.photoURL.includes("https://api.dicebear.com/9.x/identicon/svg?seed=")
      //       ) {
      //         const newavatarRef = doc(db, "users", item.id);
      //         await updateDoc(newavatarRef, { photoURL: "https://api.dicebear.com/9.x/identicon/png?seed="+item.id.substring(0,6) });
      //       }
      //     });
      //   }, 300);

      //   // });
      // });
    });

    return () => unsuscribe();
  }, []);

  // Tap a user → open the 1:1 chat with them (find or create), then navigate
  // straight into the conversation window — like a normal chat app.
  const addToFriendsList = async (e, user) => {
    e.preventDefault();
    if (openingId) return; // already opening a chat — ignore double-taps
    const me = auth.currentUser;
    if (!me?.uid || !user?.id) return;

    setOpeningId(user.id);
    try {
      const meProfile = {
        id: me.uid || "",
        email: me.email || "",
        displayName: me.displayName || "",
      };
      const otherProfile = {
        id: user.id || "",
        email: user.email || "",
        displayName: user.displayName || "",
      };

      // 1) Find an existing 1:1 chat with this user.
      const myChatsRef = collection(db, "users", me.uid, "chats");
      const { docs } = await getDocs(myChatsRef);
      let chatId = null;
      docs.forEach((d) => {
        const list = d.data().users || [];
        if (list.some((cu) => cu && cu.id === user.id)) chatId = d.id;
      });

      // 2) Create it if it doesn't exist yet.
      if (!chatId) {
        const newChat = await addDoc(collection(db, "chats"), {
          createdAt: serverTimestamp(),
          type: "private",
          // Seed participants so the chatMessageNotifier can push to BOTH users
          // from the very first message (Chat.js also maintains this on send).
          participants: [me.uid, user.id],
        });
        chatId = newChat.id;

        await Promise.all([
          setDoc(doc(db, "users", me.uid, "chats", chatId), {
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            type: "private",
            newchat: 1,
            users: [meProfile, otherProfile],
            chatId,
          }),
          setDoc(doc(db, "users", user.id, "chats", chatId), {
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            type: "private",
            users: [otherProfile, meProfile],
            chatId,
          }),
        ]);
      }

      // 3) Navigate into the conversation window (with photos for the header).
      const usersForWindow = [
        { ...meProfile, photoURL: me.photoURL || "" },
        { ...otherProfile, photoURL: avatars[user.id]?.photoURL || user.photoURL || "" },
      ];
      if (typeof openChatById === "function") {
        openChatById(chatId, usersForWindow);
      } else if (typeof setIsInUsers === "function") {
        setIsInUsers(false);
      }
    } catch (error) {
      console.error("[Users4] open chat failed:", error);
    } finally {
      setOpeningId(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setInputFocus(false);
  };

  const updateWatchList = async (msgsLength, chatId) => {
    const updatewatchListRef2 = doc(
      db,
      "users",
      auth.currentUser.uid,
      "updatewatchList",
      chatId
    );

    try {
      const thisChat2 = {
        chatId: chatId,
        updatedAt: serverTimestamp(),
        chatLength: msgsLength,
      };
      const updatewatchListDocSnap = await getDoc(updatewatchListRef2);
      if (updatewatchListDocSnap.exists()) {
        await setDoc(
          doc(db, "users", auth.currentUser.uid, "updatewatchList", chatId),
          thisChat2
        );
      } else {
        await setDoc(
          doc(db, "users", auth.currentUser.uid, "updatewatchList", chatId),
          thisChat2
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  // Check if user is online (within last 5 minutes)
  const isUserOnline = (userId) => {
    const lastOnline = avatars[userId]?.lastOnline;
    if (!lastOnline) return false;
    
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
    const lastOnlineTime = lastOnline.seconds ? lastOnline.seconds * 1000 : 0;
    return lastOnlineTime > fiveMinutesAgo;
  };

  // Filter users based on search input and exclude unknown users
  const filteredUsers = users.filter((user) => {
    // Filter out the current user
    if (user.id === auth.currentUser?.uid) return false;
    
    // Filter out users without displayName and email (would show as "Unknown User")
    if (!user.displayName && !user.email) return false;
    
    // Apply search filter if search is active
    if (!newSearch.trim()) return true; // Show all valid users if search is empty
    
    const searchLower = newSearch.toLowerCase().trim();
    const displayName = (user.displayName || "").toLowerCase();
    const email = (user.email || "").toLowerCase();
    
    return displayName.includes(searchLower) || email.includes(searchLower);
  }).sort((a, b) => {
    // Sort filtered users with online users at the top
    const aIsOnline = isUserOnline(a.id);
    const bIsOnline = isUserOnline(b.id);
    
    // Both online - maintain existing order
    if (aIsOnline && bIsOnline) {
      const aLastOnline = avatars[a.id]?.lastOnline;
      const bLastOnline = avatars[b.id]?.lastOnline;
      if (aLastOnline && bLastOnline && aLastOnline.seconds && bLastOnline.seconds) {
        return bLastOnline.seconds - aLastOnline.seconds;
      }
      return 0;
    }
    // Only a is online - a comes first
    if (aIsOnline && !bIsOnline) {
      return -1;
    }
    // Only b is online - b comes first
    if (!aIsOnline && bIsOnline) {
      return 1;
    }
    
    // Neither is online - sort by lastOnline or alphabetically
    const aLastOnline = avatars[a.id]?.lastOnline;
    const bLastOnline = avatars[b.id]?.lastOnline;
    
    if (aLastOnline && bLastOnline && aLastOnline.seconds && bLastOnline.seconds) {
      return bLastOnline.seconds - aLastOnline.seconds;
    }
    if (aLastOnline && !bLastOnline) return -1;
    if (!aLastOnline && bLastOnline) return 1;
    
    // Fall back to alphabetical sorting
    const aName = (a.displayName || a.email || '').toLowerCase();
    const bName = (b.displayName || b.email || '').toLowerCase();
    return aName.localeCompare(bName);
  });

  return (
    <>
      <div className="flex flex-col flex-auto user-panel insidechat hiddenchat">
        <div className="flex flex-col flex-auto flex-shrink-0 rounded-2xl bg-gray-100 dark:bg-gray-900 h-full panel-fade ebz7">
          <div
            className={`flex flex-row items-center h-16 rounded-xl bg-white w-full px-4 mb-4 text-enter-fit ${
              inputFocus === true ? "text-enter-full" : ""
            }`}
          >
            {/* <div>
              <button className="flex items-center justify-center  text-gray-400 hover:text-gray-600">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                  ></path>
                </svg>
              </button>
            </div> */}
            <div className="flex-grow ml-2">
              <div className="relative w-full">
                <input
                  type="text"
                  value={newSearch}
                  onFocus={() => setInputFocus(true)}
                  onChange={(event) => setNewSearch(event.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSubmit(e);
                  }}
                  className="flex w-full border rounded-xl focus:outline-none focus:border-indigo-300 pl-4 h-10"
                />
                {/* <button className="absolute flex items-center justify-center  h-full w-12 right-0 top-0 text-gray-400 hover:text-gray-600">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                </button> */}
              </div>
            </div>
            <div className="ml-2">
              <button
                onClick={(event) => handleSubmit(event)}
                className="flex items-center justify-center  rounded-xl text-white px-4 py-1 flex-shrink-0 w-4 h-8"
              >
                {/* <span>Send</span> */}
                <span className="ml-0">
                  <svg
                    className="w-4 h-4 transform rotate-45 -mt-px"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    ></path>
                  </svg>
                </span>
              </button>
            </div>
          </div>
          <div className="flex flex-col overflow-x-auto mb-4 manuf-scrollable-section">
            <div className="flex flex-row h-full">
              <div className="text-white w-full muuz-search-user-panel">
                {filteredUsers.map((user, i) => {
                  const online = isUserOnline(user.id);
                  return (
                    <div
                      className="post flex flex-row justify-between text-left text-white relative"
                      key={user.id}
                      onClick={(e) => addToFriendsList(e, user)}
                      style={{
                        cursor: openingId ? 'wait' : 'pointer',
                        opacity: openingId === user.id ? 0.6 : 1,
                        marginBottom: '12px',
                        padding: '16px',
                        borderRadius: '12px',
                        background: '#2a2a2a',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                        border: '1px solid #3a3a3a',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#3a3a3a';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#2a2a2a';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div className="flex flex-row w-full manufchat">
                        <div className="flex items-center justify-center h-12 w-12 rounded-full border overflow-hidden bg-indigo-500 flex-shrink-0">
                          <Avatar avObj={avatars} uid={user.id} />
                        </div>
                        <div className="ml-3">
                          <div className="title text-white text-left pb-1">
                            <div
                              style={{ width: "200px", overflowX: "hidden" }}
                              className="text-left text-ellipsis"
                            >
                              <strong>
                                {user.displayName || user.email || "Unknown User"}
                              </strong>
                            </div>
                          </div>
                          <p className="title text-sm text-gray-400 text-left text-ellipsis leading-6">
                            {user.email || "No email"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center absolute right-4 -top-4">
                        {openingId === user.id ? (
                          <div className="flex items-center justify-center text-xs text-white font-bold bg-indigo-500 h-6 px-2 rounded leading-none">
                            Opening…
                          </div>
                        ) : online ? (
                          <div className="flex items-center justify-center text-xs text-white font-bold bg-green-500 h-6 px-2 rounded leading-none">
                            online
                          </div>
                        ) : (
                          <div className="flex items-center justify-center text-xs text-white font-normal bg-gray-500 h-6 px-2 rounded leading-none">
                            offline
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
